import { useCallback, useEffect, useState } from 'react'
import { ApiClientError, type Variable } from '../api/types.js'
import { Alert } from '../ui/Alert.js'
import { ConfirmDialog, useConfirmation } from '../ui/ConfirmDialog.js'
import { formatMoment } from '../ui/moment.js'
import { useToaster } from '../ui/toasts.js'

/** Узкий срез клиента: экрану нужны только переменные арендатора. */
export interface VariablesClient {
  variables(): Promise<Variable[]>
  putVariable(key: string, value: string): Promise<Variable>
  removeVariable(key: string): Promise<void>
}

export interface VariablesProps {
  client: VariablesClient
  permissions: string[]
}

const CAN_WRITE = 'variables.write'

const GENERIC_FAILURE = 'Не удалось выполнить действие. Проверьте соединение и попробуйте ещё раз.'

function messageOf(cause: unknown): string {
  return cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE
}

/**
 * Переменные арендатора: то, что сценарий подставляет выражением
 * `{{ $vars.KEY }}` вместо того, чтобы повторять адрес внешней службы в
 * каждом узле.
 *
 * Значения показываются открыто — и это осознанно. Это не секреты: секреты
 * живут в credentials под конвертным шифрованием и наружу не отдаются
 * никогда. Спрятать значение здесь значило бы намекнуть, что оно защищено,
 * хотя оно лежит в базе открытым текстом.
 */
export function Variables(props: VariablesProps) {
  const { client, permissions } = props
  const canWrite = permissions.includes(CAN_WRITE)
  const toaster = useToaster()
  const удаление = useConfirmation<Variable>()

  const [variables, setVariables] = useState<Variable[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [key, setKey] = useState('')
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set())

  const load = useCallback(() => {
    let cancelled = false
    client
      .variables()
      .then((items) => {
        if (cancelled) return
        setVariables(items)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(messageOf(cause))
      })
    return () => {
      cancelled = true
    }
  }, [client])

  useEffect(load, [load])

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const saved = await client.putVariable(key, value)
      // Запись по ключу: повтор того же ключа меняет значение, а не заводит
      // вторую переменную — иначе подстановка стала бы неоднозначной.
      setVariables((items) => [
        saved,
        ...(items ?? []).filter((item) => item.key !== saved.key),
      ])
      setShowForm(false)
      setKey('')
      setValue('')
      toaster.succeeded(`Переменная ${saved.key} сохранена`)
    } catch (cause: unknown) {
      setError(messageOf(cause))
    } finally {
      setSaving(false)
    }
  }

  async function remove(target: string) {
    удаление.cancel()
    setBusy((current) => new Set(current).add(target))
    setError(null)
    try {
      await client.removeVariable(target)
      setVariables((items) => (items ?? []).filter((item) => item.key !== target))
      toaster.succeeded(`Переменная ${target} удалена`)
    } catch (cause: unknown) {
      setError(messageOf(cause))
    } finally {
      setBusy((current) => {
        const next = new Set(current)
        next.delete(target)
        return next
      })
    }
  }

  return (
    <section aria-label="Переменные">
      {error !== null && <Alert>{error}</Alert>}

      {canWrite && (
        <div className="card__actions">
          <button type="button" className="btn" onClick={() => setShowForm(!showForm)}>
            Добавить переменную
          </button>
        </div>
      )}

      {showForm && (
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault()
            void save()
          }}
        >
          <label htmlFor="переменная-ключ">Ключ</label>
          <input
            id="переменная-ключ"
            value={key}
            onChange={(event) => setKey(event.target.value)}
            required
          />
          <p className="form__hint">
            {key.trim() === ''
              ? 'В сценарии к переменной обращаются как {{ $vars.КЛЮЧ }}.'
              : `В сценарии: {{ $vars.${key.trim()} }}`}
          </p>

          <label htmlFor="переменная-значение">Значение</label>
          <input
            id="переменная-значение"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            required
          />

          <button type="submit" className="btn" disabled={saving || key.trim() === ''}>
            Сохранить
          </button>
        </form>
      )}

      {variables === null && error === null && <p>Загрузка переменных…</p>}

      {variables !== null && variables.length === 0 && (
        <p>
          Переменных пока нет. Переменная — общее значение для всех сценариев арендатора: адрес
          службы, название компании, номер для пересылки. Задайте его один раз здесь и
          подставляйте в узлах выражением <code>{'{{ $vars.КЛЮЧ }}'}</code> — менять придётся
          тоже в одном месте. Пароли и ключи сюда не кладут: значения хранятся открыто, для
          секретов есть Credentials.
        </p>
      )}

      {variables !== null && variables.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Ключ</th>
              <th scope="col">Значение</th>
              <th scope="col">Изменена</th>
              {canWrite && <th scope="col">Действия</th>}
            </tr>
          </thead>
          <tbody>
            {variables.map((variable) => (
              <tr key={variable.key}>
                <td>
                  {variable.key}
                  <span className="table__hint">{`{{ $vars.${variable.key} }}`}</span>
                </td>
                <td>{variable.value}</td>
                <td>{formatMoment(variable.updatedAt)}</td>
                {canWrite && (
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      disabled={busy.has(variable.key)}
                      onClick={() => удаление.ask(variable)}
                    >
                      Удалить
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {удаление.pending !== null && (
        <ConfirmDialog
          title={`Удалить переменную ${удаление.pending.key}?`}
          confirmLabel="Удалить"
          busy={busy.has(удаление.pending.key)}
          onCancel={удаление.cancel}
          onConfirm={() => {
            if (удаление.pending !== null) void remove(удаление.pending.key)
          }}
        >
          <p>
            Узлы, подставляющие <code>{`{{ $vars.${удаление.pending.key} }}`}</code>, останутся без
            значения: сценарий не найдёт его и остановится на этом шаге. Портал не знает, кто из
            них ею пользуется, — проверить это можно только в самих сценариях.
          </p>
        </ConfirmDialog>
      )}
    </section>
  )
}
